import { organizationGenerationLoginLink } from "@dutyon/apis";
import { AnalyticsTracker, AuthGuard, modals, RouteErrorBoundary, Text } from "@dutyon/ui-kit";
import { lazy } from "react";
import { type RouteObject, useLocation } from "react-router-dom";
import { DutyAutoGenerationGuard } from "./components/DutyAutoGenerationGuard";
import { RoleGuard } from "./components/RoleGuard";
import { isHiraCareEnabled } from "./lib/features";

const Layout = lazy(() => import("./Layout").then((module) => ({ default: module.Layout })));

const EmailLoginPage = lazy(() =>
  import("./pages/auth/EmailLoginPage").then((m) => ({ default: m.EmailLoginPage })),
);
const SignUpPage = isHiraCareEnabled
  ? lazy(() => import("./pages/auth/SignUpPage").then((m) => ({ default: m.SignUpPage })))
  : EmailLoginPage;
const LoginPage = lazy(() =>
  import("./pages/auth/LoginPage").then((m) => ({ default: m.LoginPage })),
);
const InvalidLinkPage = lazy(() =>
  import("./pages/common/InvalidLinkPage").then((m) => ({ default: m.InvalidLinkPage })),
);
const NotFoundPage = lazy(() =>
  import("./pages/common/NotFoundPage").then((m) => ({ default: m.NotFoundPage })),
);
const DashboardPage = lazy(() =>
  import("./pages/dashboard/DashboardPage").then((m) => ({ default: m.DashboardPage })),
);
const AttendanceManagementPage = lazy(() =>
  import("./pages/attendance/AttendanceManagementPage").then((m) => ({
    default: m.AttendanceManagementPage,
  })),
);
const DutyManagementPage = lazy(() =>
  import("./pages/duty/DutyManagementPage").then((m) => ({ default: m.DutyManagementPage })),
);
const GenerationJobPage = lazy(() =>
  import("./pages/GenerationJobPage").then((m) => ({ default: m.GenerationJobPage })),
);
const DutyGeneratePage = lazy(() =>
  import("./pages/duty/DutyGeneratePage").then((m) => ({ default: m.DutyGeneratePage })),
);
const DutyDraftsPage = lazy(() =>
  import("./pages/duty/DutyDraftsPage").then((m) => ({ default: m.DutyDraftsPage })),
);
const DutyRequestCreatePage = lazy(() =>
  import("./pages/duty/DutyRequestCreatePage").then((m) => ({ default: m.DutyRequestCreatePage })),
);
const DutyRequestDetailPage = lazy(() =>
  import("./pages/duty/DutyRequestDetailPage").then((m) => ({ default: m.DutyRequestDetailPage })),
);
const DutyRequestEditPage = lazy(() =>
  import("./pages/duty/DutyRequestEditPage").then((m) => ({ default: m.DutyRequestEditPage })),
);
const DutyRequestsPage = lazy(() =>
  import("./pages/duty/DutyRequestsPage").then((m) => ({ default: m.DutyRequestsPage })),
);
const HolidayManagementPage = lazy(() =>
  import("./pages/holiday/HolidayManagementPage").then((m) => ({
    default: m.HolidayManagementPage,
  })),
);
const HiraCarePage = isHiraCareEnabled
  ? lazy(() => import("./pages/hiracare/HiraCarePage").then((m) => ({ default: m.HiraCarePage })))
  : NotFoundPage;
const HiraCareHomePage = isHiraCareEnabled
  ? lazy(() =>
      import("./pages/hiracare/HiraCareHomePage").then((m) => ({ default: m.HiraCareHomePage })),
    )
  : NotFoundPage;
const InquiryCreatePage = lazy(() =>
  import("./pages/inquiry/InquiryCreatePage").then((m) => ({ default: m.InquiryCreatePage })),
);
const InquiryDetailPage = lazy(() =>
  import("./pages/inquiry/InquiryDetailPage").then((m) => ({ default: m.InquiryDetailPage })),
);
const InquiryListPage = lazy(() =>
  import("./pages/inquiry/InquiryListPage").then((m) => ({ default: m.InquiryListPage })),
);
const LandingLayout = lazy(() =>
  import("./pages/landing/intro").then((m) => ({ default: m.LandingLayout })),
);
const LandingHomePage = lazy(() =>
  import("./pages/landing/intro").then((m) => ({ default: m.HomePage })),
);
const LandingServicePage = lazy(() =>
  import("./pages/landing/intro").then((m) => ({ default: m.ServicePage })),
);
const LandingFaqPage = lazy(() =>
  import("./pages/landing/intro").then((m) => ({ default: m.FaqPage })),
);
const LandingPageAbout = lazy(() =>
  import("./pages/landing/LandingPageAbout").then((m) => ({ default: m.LandingPageAbout })),
);
const PrivacyPage = lazy(() =>
  import("./pages/landing/PrivacyPage").then((m) => ({ default: m.PrivacyPage })),
);
const TermsPage = lazy(() =>
  import("./pages/landing/TermsPage").then((m) => ({ default: m.TermsPage })),
);
const ServiceTermsPage = lazy(() =>
  import("./pages/landing/PublishedTermsPage").then((m) => ({
    default: m.ServiceTermsPage,
  })),
);
const MarketingTermsInfoPage = lazy(() =>
  import("./pages/landing/PublishedTermsPage").then((m) => ({
    default: m.MarketingTermsInfoPage,
  })),
);
const TrialApplicationPage = lazy(() =>
  import("./pages/landing/TrialApplicationPage").then((m) => ({ default: m.TrialApplicationPage })),
);
const NoticeCreatePage = lazy(() =>
  import("./pages/notice/NoticeCreatePage").then((m) => ({ default: m.NoticeCreatePage })),
);
const NoticeEditPage = lazy(() =>
  import("./pages/notice/NoticeEditPage").then((m) => ({ default: m.NoticeEditPage })),
);
const NoticesPage = lazy(() =>
  import("./pages/notice/NoticesPage").then((m) => ({ default: m.NoticesPage })),
);
const JobPositionManagementPage = lazy(() =>
  import("./pages/nurse/JobPositionManagementPage").then((m) => ({
    default: m.JobPositionManagementPage,
  })),
);
const NurseCreatePage = lazy(() =>
  import("./pages/nurse/NurseCreatePage").then((m) => ({ default: m.NurseCreatePage })),
);
const NurseDetailPage = lazy(() =>
  import("./pages/nurse/NurseDetailPage").then((m) => ({ default: m.NurseDetailPage })),
);
const NurseManagementPage = lazy(() =>
  import("./pages/nurse/NurseManagementPage").then((m) => ({ default: m.NurseManagementPage })),
);
const StatisticsPage = lazy(() =>
  import("./pages/stats/StatisticsPage").then((m) => ({ default: m.StatisticsPage })),
);
const TeamCreatePage = lazy(() =>
  import("./pages/team/TeamCreatePage").then((m) => ({ default: m.TeamCreatePage })),
);
const TeamDetailPage = lazy(() =>
  import("./pages/team/TeamDetailPage").then((m) => ({ default: m.TeamDetailPage })),
);
const TeamManagementPage = lazy(() =>
  import("./pages/team/TeamManagementPage").then((m) => ({ default: m.TeamManagementPage })),
);
const MyInfoPage = lazy(() =>
  import("./pages/user/MyInfoPage").then((m) => ({ default: m.MyInfoPage })),
);
const PublicWantedRequestPage = lazy(() =>
  import("./pages/wanted/PublicWantedRequestPage").then((m) => ({
    default: m.PublicWantedRequestPage,
  })),
);
const WantedManagementPage = lazy(() =>
  import("./pages/wanted/WantedManagementPage").then((m) => ({ default: m.WantedManagementPage })),
);
const WorkShiftCreatePage = lazy(() =>
  import("./pages/work-shift/WorkShiftCreatePage").then((m) => ({
    default: m.WorkShiftCreatePage,
  })),
);
const WorkShiftEditPage = lazy(() =>
  import("./pages/work-shift/WorkShiftEditPage").then((m) => ({ default: m.WorkShiftEditPage })),
);
const WorkShiftListPage = lazy(() =>
  import("./pages/work-shift/WorkShiftListPage").then((m) => ({ default: m.WorkShiftListPage })),
);

function DashboardAuthGuard({ fallbackPath }: { fallbackPath?: string }) {
  const location = useLocation();
  return (
    <AuthGuard
      allowedRoles={["HOSPITAL_ADMIN", "TEAM_ADMIN"]}
      fallbackPath={fallbackPath ?? organizationGenerationLoginLink(location.pathname)}
      onUnauthorized={(decoded, actions) => {
        if (decoded?.role === "SUPER_ADMIN") {
          modals.open({
            title: "접근 제한",
            children: (
              <Text size="sm">
                최고 관리자는 대시보드에 접근할 수 없습니다. 백오피스를 이용해주세요.
              </Text>
            ),
            centered: true,
            withCloseButton: true,
            onClose: () => {
              actions.clearToken();
              actions.reject();
            },
          });
        } else {
          modals.open({
            title: "접근 제한",
            children: (
              <Text size="sm">권한이 없습니다. 관리자에게 문의해주세요. ({decoded?.role})</Text>
            ),
            centered: true,
            withCloseButton: true,
            onClose: () => {
              actions.clearToken();
              actions.reject();
            },
          });
        }
      }}
    />
  );
}

export const routes: RouteObject[] = [
  {
    element: <AnalyticsTracker />,
    errorElement: (
      <RouteErrorBoundary
        homePath="/"
        serviceName="DutyOn Dashboard"
        logoSrc="/logo.png"
        homeLabel="대시보드 홈"
      />
    ),
    children: [
      {
        path: "/login",
        element: <LoginPage />,
      },
      {
        path: "/login/email",
        element: <EmailLoginPage />,
      },
      ...(isHiraCareEnabled ? [{ path: "/signup", element: <SignUpPage /> }] : []),
      {
        path: "/company/:companyId/wanted-request",
        element: <PublicWantedRequestPage />,
      },
      {
        path: "/company/:companyId/teams/:teamId/wanted-request",
        element: <PublicWantedRequestPage />,
      },
      {
        path: "/invalid-link",
        element: <InvalidLinkPage />,
      },
      {
        path: "/intro",
        element: <LandingLayout />,
        children: [
          { index: true, element: <LandingHomePage /> },
          { path: "service", element: <LandingServicePage /> },
          { path: "faq", element: <LandingFaqPage /> },
        ],
      },
      {
        path: "/about",
        element: <LandingPageAbout />,
      },
      {
        path: "/trial-application",
        element: <TrialApplicationPage />,
      },
      {
        path: "/terms",
        element: <TermsPage />,
      },
      {
        path: "/terms/service",
        element: <ServiceTermsPage />,
      },
      {
        path: "/terms/marketing-info",
        element: <MarketingTermsInfoPage />,
      },
      {
        path: "/privacy",
        element: <PrivacyPage />,
      },
      {
        element: <DashboardAuthGuard fallbackPath="/intro" />,
        children: [
          {
            element: <Layout />,
            children: [{ path: "/", element: <DashboardPage /> }],
          },
        ],
      },
      {
        element: <DashboardAuthGuard />,
        children: [
          {
            element: <Layout />,
            children: [
              { path: "/generation-jobs/:jobId", element: <GenerationJobPage /> },
              { path: "/nurses", element: <NurseManagementPage /> },
              {
                path: "/positions",
                element: (
                  <RoleGuard allowedRoles={["HOSPITAL_ADMIN"]}>
                    <JobPositionManagementPage />
                  </RoleGuard>
                ),
              },
              { path: "/nurses/create", element: <NurseCreatePage /> },
              { path: "/nurses/:id", element: <NurseDetailPage /> },
              {
                path: "/teams",
                element: (
                  <RoleGuard allowedRoles={["HOSPITAL_ADMIN"]}>
                    <TeamManagementPage />
                  </RoleGuard>
                ),
              },
              {
                path: "/teams/create",
                element: (
                  <RoleGuard allowedRoles={["HOSPITAL_ADMIN"]}>
                    <TeamCreatePage />
                  </RoleGuard>
                ),
              },
              {
                path: "/teams/:id",
                element: (
                  <RoleGuard allowedRoles={["HOSPITAL_ADMIN"]}>
                    <TeamDetailPage />
                  </RoleGuard>
                ),
              },
              // 근무타입
              { path: "/work-shifts", element: <WorkShiftListPage /> },
              { path: "/work-shifts/create", element: <WorkShiftCreatePage /> },
              { path: "/work-shifts/:id/edit", element: <WorkShiftEditPage /> },

              // 원티드
              { path: "/wanteds", element: <WantedManagementPage /> },

              { path: "/duties", element: <DutyManagementPage /> },
              { path: "/duty-drafts", element: <DutyDraftsPage /> },
              { path: "/attendances", element: <AttendanceManagementPage /> },
              { path: "/stats", element: <StatisticsPage /> },
              ...(isHiraCareEnabled
                ? [
                    {
                      path: "/hiracare",
                      element: (
                        <RoleGuard allowedRoles={["HOSPITAL_ADMIN", "SUPER_ADMIN", "NORMAL_USER"]}>
                          <HiraCareHomePage />
                        </RoleGuard>
                      ),
                    },
                    {
                      path: "/hiracare/reports/:reportId",
                      element: (
                        <RoleGuard allowedRoles={["HOSPITAL_ADMIN", "SUPER_ADMIN", "NORMAL_USER"]}>
                          <HiraCarePage />
                        </RoleGuard>
                      ),
                    },
                  ]
                : []),

              // 듀티 신청
              { path: "/duty-requests", element: <DutyRequestsPage /> },
              { path: "/duty-requests/:id", element: <DutyRequestDetailPage /> },
              { path: "/duty-requests/:id/edit", element: <DutyRequestEditPage /> },
              { path: "/duty-requests/new", element: <DutyRequestCreatePage /> },
              {
                path: "/duty-generate",
                element: (
                  <DutyAutoGenerationGuard>
                    <DutyGeneratePage />
                  </DutyAutoGenerationGuard>
                ),
              },

              // 문의하기
              { path: "/inquiries", element: <InquiryListPage /> },
              { path: "/inquiries/new", element: <InquiryCreatePage /> },
              { path: "/inquiries/:id", element: <InquiryDetailPage /> },

              // 기타 페이지
              { path: "/my-info", element: <MyInfoPage /> },
              { path: "/terms", element: <TermsPage /> },
              { path: "/privacy", element: <TermsPage /> },
              { path: "/opensource", element: <TermsPage /> },
              {
                path: "/services/notices",
                element: (
                  <RoleGuard allowedRoles={["HOSPITAL_ADMIN"]}>
                    <NoticesPage />
                  </RoleGuard>
                ),
              },
              {
                path: "/services/notices/create",
                element: (
                  <RoleGuard allowedRoles={["HOSPITAL_ADMIN"]}>
                    <NoticeCreatePage />
                  </RoleGuard>
                ),
              },
              {
                path: "/services/notices/:id/edit",
                element: (
                  <RoleGuard allowedRoles={["HOSPITAL_ADMIN"]}>
                    <NoticeEditPage />
                  </RoleGuard>
                ),
              },
              {
                path: "/holidays",
                element: (
                  <RoleGuard allowedRoles={["HOSPITAL_ADMIN"]}>
                    <HolidayManagementPage />
                  </RoleGuard>
                ),
              },
            ],
          },
        ],
      },
      {
        path: "*",
        element: <NotFoundPage />,
      },
    ],
  },
];
